import { ImageLoader, ImageLoaderConfig } from "@angular/common";

/**
 * This class build a valid scene 7 url from a list of optional parameters
 *
 * @deprecated Use new Scene7 loader and `<h-image>` component from @hermes/aphrodite/image
 */
export class Scene7ImageUrlBuilder {
    private SCENE7_IMAGE_BACKGROUND_COLOR = "$product_item_grid_g$";
    private TRANSPARENT_THUMBNAIL = "$transparent_thumbnail$";

    constructor(
        private url: string,
        private config: ImageConfig,
    ) {}

    public build(applyBackground = false, applyTransparency = false): string {
        if (
            this.config.sourceSet &&
            ((this.config.width && this.config.height) || this.config.imageSize)
        ) {
            return this.generateSourceSetUrl(
                applyBackground,
                applyTransparency,
            );
        }
        return this.getURLWithScene7Config(
            this.url,
            this.config,
            applyBackground,
            applyTransparency,
        );
    }

    /**
     * Transforms the configuration into valid get parameters
     */
    private getURLWithScene7Config(
        urlWithoutScene7Config: string,
        config: ImageConfig,
        applyBackground = false,
        applyTransparency = false,
    ): string {
        const url: URL = urlWithoutScene7Config.startsWith("//")
            ? new URL(`https:${urlWithoutScene7Config}`)
            : new URL(urlWithoutScene7Config);
        Object.entries(toScene7Config(config)).forEach(([key, value]) => {
            url.searchParams.append(key, value);
        });

        switch (true) {
            case applyBackground: {
                return `${url.toString()}&${this.SCENE7_IMAGE_BACKGROUND_COLOR}`;
            }
            case applyTransparency: {
                return `${url.toString()}&${this.TRANSPARENT_THUMBNAIL}`;
            }
            default: {
                return url.toString();
            }
        }
    }

    /**
     * Generate a url for the sourceSet
     */
    private generateSourceSetUrl(
        applyBackground = false,
        applyTransparency = false,
    ): string {
        const normalSizeUrl = this.getURLWithScene7Config(
            this.url,
            this.config,
        );
        const zoomSizeurl = this.getURLWithScene7Config(this.url, {
            ...this.config,
            ...(this.config.imageSize && {
                imageSize: this.config.imageSize * 2,
            }),
            ...(this.config.width && { width: this.config.width * 2 }),
            ...(this.config.height && { height: this.config.height * 2 }),
        });

        switch (true) {
            case applyBackground: {
                return `${normalSizeUrl}&${this.SCENE7_IMAGE_BACKGROUND_COLOR} 1x, ${zoomSizeurl}&${this.SCENE7_IMAGE_BACKGROUND_COLOR} 2x`;
            }
            case applyTransparency: {
                return `${normalSizeUrl}&${this.TRANSPARENT_THUMBNAIL} 1x, ${zoomSizeurl}&${this.TRANSPARENT_THUMBNAIL} 2x`;
            }
            default: {
                return `${normalSizeUrl} 1x, ${zoomSizeurl} 2x`;
            }
        }
    }
}

const MAX_SCENE7_WIDTH = 3000;
const MAX_SCENE7_HEIGHT = 3000;

/**
 * Builds width and height config for Scene7
 */
const buildDimensionsConfig = (config: ImageConfig): Record<string, string> => {
    const result: Record<string, string> = {};

    if (config.width) {
        // Avoid dimension error due to Scene 7 limitation
        let finalWidth = config.width;
        if (finalWidth > MAX_SCENE7_WIDTH) {
            finalWidth = MAX_SCENE7_WIDTH;
        }
        // Apply width limitation if asked
        if (config.maxWidth && finalWidth > config.maxWidth) {
            finalWidth = config.maxWidth;
        }
        result["wid"] = finalWidth.toString();
    }

    // Fallback to aspectRatio calculation if height is not explicitly fixed
    let height = config.height;
    if (height === undefined && config.width && config.aspectRatio) {
        height = Math.round(config.width / config.aspectRatio);
    }
    if (height) {
        // Avoid dimension error due to Scene 7 limitation
        const finalHeight = Math.min(height, MAX_SCENE7_HEIGHT);
        result["hei"] = finalHeight.toString();
    }

    return result;
};

/**
 * https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/c-command-reference.html?lang=en
 */
const toScene7Config = (config: ImageConfig): Record<string, string> => {
    let scene7config: Record<string, string> = buildDimensionsConfig(config);
    if (config.imageSize) {
        scene7config = {
            ...scene7config,
            size: config.imageSize.toString(),
        };
    }
    if (config.quality) {
        scene7config = {
            ...scene7config,
            qlt: config.quality,
        };
    }
    if (config.opSharpen) {
        scene7config = {
            ...scene7config,
            op_sharpen: config.opSharpen.toString(),
        };
    }
    if (config.resmode) {
        scene7config = {
            ...scene7config,
            resMode: config.resmode,
        };
    }
    if (config.align) {
        scene7config = {
            ...scene7config,
            align: config.align,
        };
    }
    if (config.format) {
        scene7config = {
            ...scene7config,
            fmt: config.format,
        };
    }
    if (config.fit) {
        scene7config = {
            ...scene7config,
            fit: config.upscale ? `${config.fit},1` : config.fit,
        };
    }
    if (config.defaultImage) {
        scene7config = {
            ...scene7config,
            defaultImage: config.defaultImage,
        };
    }
    if (config.extra) {
        for (const [key, value] of Object.entries(config.extra)) {
            scene7config[key] = value.toString();
        }
    }

    return scene7config;
};

/**
 * @deprecated Use new Scene7 loader and `<h-image>` component from @hermes/aphrodite/image
 */
export interface ImageConfig {
    width?: number;
    maxWidth?: number;
    height?: number;
    imageSize?: number;
    quality?: string;
    sourceSet?: boolean;
    align?: string;
    opSharpen?: 0 | 1;
    format?: string;
    resmode?: "bilin" | "bicub" | "sharp2" | "bisharp" | "sharp";
    aspectRatio?: number;
    fit?: "hfit" | "crop" | "constrain" | "fit" | "wrap";
    upscale?: boolean;
    defaultImage?: string;
    extra?: Record<string, string | number | boolean>;
    background?: boolean;
    transparency?: boolean;
}

/**
 * @deprecated Use new Scene7 loader and `<h-image>` component from @hermes/aphrodite/image
 */
export const scene7ImageLoader: ImageLoader = (
    imageLoaderConfig: ImageLoaderConfig,
): string => {
    const imageConfig: ImageConfig = {
        width: imageLoaderConfig.width,
        ...imageLoaderConfig.loaderParams,
    };

    const builder = new Scene7ImageUrlBuilder(
        imageLoaderConfig.src,
        imageConfig,
    );
    return builder.build(imageConfig.background, imageConfig.transparency);
};
